import { ref } from 'vue'
import { coreFetch } from '@/lib/http.js'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { offline } from '@/lib/offline.js'
import { setSocket } from '@/lib/socketId.js'

/**
 * The websocket (Laravel Reverb) only says "pull now" (addictive-offline broadcasts
 * `offline.changes` per tenant); the data still comes through the feed, which applies
 * visibility. Without a key configured there is no socket and devices pull on their interval.
 */
let echo = null
let tenant = null
/** Whether the socket is set up (the shell sets it up after the page inside it is mounted). */
export const echoReady = ref(false)

export function connectEcho(me) {
  const key = import.meta.env.VITE_REVERB_APP_KEY
  if (!key || !me?.tenant?.uuid || echo) return null
  window.Pusher = Pusher
  // Without an explicit host the socket goes through the page's own origin (production:
  // nginx proxies /app/ to Reverb), so scheme, host and port are the page's.
  const explicit = Boolean(import.meta.env.VITE_REVERB_HOST)
  const tls = explicit ? import.meta.env.VITE_REVERB_SCHEME === 'https' : window.location.protocol === 'https:'
  const port = Number(explicit ? import.meta.env.VITE_REVERB_PORT || (tls ? 443 : 80) : window.location.port || (tls ? 443 : 80))
  echo = new Echo({
    broadcaster: 'reverb',
    key,
    wsHost: explicit ? import.meta.env.VITE_REVERB_HOST : window.location.hostname,
    wsPort: port,
    wssPort: port,
    forceTLS: tls,
    enabledTransports: ['ws', 'wss'],
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        coreFetch('/broadcasting/auth', { method: 'POST', body: { socket_id: socketId, channel_name: channel.name } })
          .then(async (r) => (r.ok ? callback(null, await r.json()) : callback(new Error(`HTTP ${r.status}`), null)))
          .catch((e) => callback(e, null))
      },
    }),
  })
  tenant = me.tenant.uuid
  echo.private(`offline.tenant.${me.tenant.uuid}`).listen('.offline.changes', () => offline.notify())
  setSocket(() => echo?.socketId() || null)
  echoReady.value = true
  return echo
}

/**
 * An offer open in the editor (D-206): who else has it open (`people`, each {uuid, name}, kept
 * current), what they say of themselves (`said`: a person's {uuid, editing}), and the offer
 * changed by someone (`changed`: {version, fields, by, at} — `blocks` among the fields when it was
 * its document, D-345). Returns {say, leave}; null without a socket.
 */
export function joinOffer(uuid, { people, said, changed }) {
  if (!echo) return null
  const name = `offer.${uuid}`
  let list = []
  const set = (next) => {
    list = next
    people(list)
  }
  const channel = echo.join(name)
    .here((members) => set(members))
    .joining((member) => set([...list.filter((m) => m.uuid !== member.uuid), member]))
    .leaving((member) => set(list.filter((m) => m.uuid !== member.uuid)))
    .listen('.offer.changed', (e) => changed(e))
    .listenForWhisper('editing', (e) => said(e))
  return {
    say: (what) => channel.whisper('editing', what),
    leave: () => echo?.leave(name),
  }
}

/**
 * The catalogue's new versions in use (D-345, stage 3): `fn` given {items: [{type, uuid, label}],
 * by, at} — components, sections, modules, SLA tables, coverage tariffs. Returns the unbinding;
 * null without a socket.
 */
export function onCatalogue(fn) {
  if (!echo || !tenant) return null
  const channel = echo.private(`catalogue.${tenant}`).listen('.catalogue.changed', fn)
  return () => channel.stopListening('.catalogue.changed', fn)
}

/**
 * `fn` called each time the socket is connected again after it was lost (the network, the device
 * asleep): what was said meanwhile never came, so the page asks (D-345). Returns the unbinding.
 */
export function onSocketBack(fn) {
  const connection = echo?.connector?.pusher?.connection
  if (!connection) return null
  let lost = false
  const changed = ({ current }) => {
    if (current === 'connected' && lost) {
      lost = false
      fn()
    } else if (current !== 'connected') lost = true
  }
  connection.bind('state_change', changed)
  return () => connection.unbind('state_change', changed)
}

export function disconnectEcho() {
  echo?.disconnect()
  echo = null
  tenant = null
  setSocket(null)
  echoReady.value = false
}
